<template>
  <div class="page-create">
    <div class="view container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb" />
      <Pricing
        tag="form"
        :value="['page']"
        :name="pageCategoryLabel"
        class="page-create__form"
        @submit.prevent="submit"
        @assisted="$ev => (assisted = $ev)"
      >
        <template #default>
          <div class="page-create__inputs">
            <InputComponent
              v-model="data.name"
              data-field="name"
              :label="labels.name"
              required
              :is-valid="validator.fields.name.valid"
              :validation-message="validator.fields.name.error"
            />

            <Billing v-model="data.billing" :validator="validator" />

            <InputComponent v-if="profiles.length" :label="labels.profile" data-field="profile">
              <template #default>
                <Select v-model="data.profile">
                  <option :value="null" style="opacity: 0.5">{{ $t('none') }}</option>
                  <option v-for="profile in profiles" :key="profile.id" :value="profile.id">{{ profile.name }}</option>
                </Select>
              </template>
            </InputComponent>
          </div>
        </template>

        <template #actions="{ disabled }">
          <ButtonComponent
            :label="$t('orderItem', { item: '' })"
            :icon="['fal', 'plus']"
            theme="primary"
            confirm
            type="submit"
            :disabled="!validator.passes || disabled || assisted"
            :promise="promises.mutate"
          />
        </template>
      </Pricing>
    </div>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import ModelMixin from '@/mixins/Model'
import Validator from '@/mixins/validator-v2'
import Pricing from '@/components/Pricing'
import { mapState, mapActions } from 'vuex'
import { mapi18n } from '@/i18n'
import AddressComponent from '@/components/Address'
import Billing from '@/components/Billing/Component'
import Select from '@/components/Select/Select'

export default {
  mixins: [
    ModelMixin({
      source: false,
      model: {
        name: null,
        billing: null,
        profile: null,
      },
    }),
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
  ],

  data() {
    return {
      assisted: false,
    }
  },
  computed: {
    ...mapState('Page', ['promises']),

    ...mapState('ModelTemplate', {
      profiles(state) {
        return [...this.legacyProfiles, ...state.items]
      },
    }),

    legacyProfiles() {
      let category = this.$route.query.category

      return (this.$store.state.enums.Profiles || []).filter(i => {
        return i.type === (category && category !== 'default' ? `page:${category}` : 'page')
      })
    },

    ...mapi18n('labels', [
      { name: 'name' },
      { profile: ['profile', 1] },
      { billing: 'billing' },
      { 'billing.company': 'company' },
      { 'billing.email': 'email' },
      { 'billing.organizationNumber': 'orgNo' },
    ]),

    rules() {
      let base = {
        name: 'required',
      }

      if (this.data.billing) {
        base = {
          ...base,
          'billing.company': 'required',
          'billing.organizationNumber': 'required',
          'billing.email': 'required',
        }
      }

      return base
    },
    pageCategoryLabel() {
      let label = this.$store.getters['Page/getCategory'](this.$route.query.category).label
      return label === 'page' ? this.$tc('page', 2) : label
    },
    breadcrumb() {
      return [
        { title: this.pageCategoryLabel, action: { name: 'pages', query: this.$route.query } },
        { title: this.$metaInfo.title },
      ]
    },
  },
  methods: {
    ...mapActions('Page', ['mutate']),

    ...mapActions('ModelTemplate', {
      fetch(dispatch) {
        return dispatch('fetch', [
          {
            filter: {
              ofType: 'Page',
              categories: this.$route.query.category === 'default' ? null : this.$route.query.category,
            },
          },
          {
            query: 'ModelTemplateProfiles',
          },
        ])
      },
    }),

    submit() {
      this.mutate([{ data: this.data, profile: this.profile }, { transformPayload: false }]).then(data => {
        const query = {}

        if (!this.data.profile) {
          query.action = '/api/action/preset/example-data'
        }

        this.$router.replace({
          name: 'page-edit',
          params: { id: data.id },
          query: {
            ...this.$route.query,
            ...query,
          },
        })

        return data
      })
    },
  },

  created() {
    this.fetch().then(data => {
      const defaultProfile = [...this.legacyProfiles, ...data].find(d => d.default || d.isDefault)
      if (!defaultProfile) return

      this.data.profile = defaultProfile.id
    })
  },

  metaInfo() {
    return {
      title: `${this.$t('create')} ${this.pageCategoryLabel.toLowerCase()}`,
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    Card,
    Pricing,
    AddressComponent,
    Billing,
    Select,
  },
}
</script>

<style lang="scss">
.page-create {
  background-color: $background-grey;
  padding-bottom: 0;

  &__form {
    margin: 0 auto;
    max-width: 700px;
  }

  &__summary {
    flex-grow: 1;

    &-value {
      font-size: 1.5rem;
      font-weight: bold;
    }
  }

  &__inputs {
    display: grid;
    grid-gap: 1rem;
  }
}
</style>
